import { useState } from "react";
import { MapPin, Phone, Mail, Globe, Send } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { COMPANY, IMAGES } from "@/data/content";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { nome: "", email: "", telefono: "", messaggio: "" };

export default function ContattiPage() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/contatti`, form);
      setSent(true);
      setForm(INITIAL);
      if (res.data?.email_sent === false) {
        toast.success("Messaggio ricevuto e registrato. Per risposte immediate chiamaci al " + COMPANY.phone + ".");
      } else {
        toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Invio non riuscito. Riprova oppure chiamaci al " + COMPANY.phone
      );
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-brand-slate/60 px-4 py-3.5 text-sm text-white placeholder:text-white/35 outline-none transition focus:border-brand-ice focus:ring-1 focus:ring-brand-ice";

  return (
    <>
      <PageHero
        eyebrow="Contatti"
        lines={["Parliamo", <span key="p" className="text-brand-ice">del tuo progetto</span>]}
        description="Siamo a Savoia di Lucania, in provincia di Potenza. Scrivici o chiamaci: rispondiamo con la rapidità che da sempre contraddistingue i nostri cantieri."
        image={IMAGES.slopeRock}
      />

      <section className="py-20 lg:py-28" data-testid="contatti-section">
        <div className="mx-auto grid w-full max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-[1fr_1.2fr] lg:gap-24">
          <div>
            <Eyebrow>Recapiti</Eyebrow>
            <div className="mt-10 space-y-8">
              <FadeIn>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent("Corso Garibaldi 10, Savoia di Lucania (PZ)")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-address-link"
                  className="group flex items-start gap-5"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-brand-cyan/40 text-brand-ice transition group-hover:bg-brand-cyan group-hover:text-brand-teal">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Sede</span>
                    <span className="mt-2 block text-base text-white transition group-hover:text-brand-ice">
                      {COMPANY.address}
                    </span>
                  </span>
                </a>
              </FadeIn>
              <FadeIn delay={0.08}>
                <a href={COMPANY.phoneHref} data-testid="contact-phone-link" className="group flex items-start gap-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-brand-cyan/40 text-brand-ice transition group-hover:bg-brand-cyan group-hover:text-brand-teal">
                    <Phone className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Telefono</span>
                    <span className="mt-2 block text-base text-white transition group-hover:text-brand-ice">
                      {COMPANY.phone}
                    </span>
                  </span>
                </a>
              </FadeIn>
              <FadeIn delay={0.16}>
                <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="group flex items-start gap-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-brand-cyan/40 text-brand-ice transition group-hover:bg-brand-cyan group-hover:text-brand-teal">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Email</span>
                    <span className="mt-2 block break-all text-base text-white transition group-hover:text-brand-ice">
                      {COMPANY.email}
                    </span>
                  </span>
                </a>
              </FadeIn>
              <FadeIn delay={0.24}>
                <a
                  href={COMPANY.websiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-website-link"
                  className="group flex items-start gap-5"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-brand-cyan/40 text-brand-ice transition group-hover:bg-brand-cyan group-hover:text-brand-teal">
                    <Globe className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Sito</span>
                    <span className="mt-2 block text-base text-white transition group-hover:text-brand-ice">
                      {COMPANY.website}
                    </span>
                  </span>
                </a>
              </FadeIn>
            </div>
          </div>

          <FadeIn delay={0.1}>
            <form
              onSubmit={onSubmit}
              className="border border-white/10 bg-brand-card p-8 sm:p-10"
              data-testid="contact-form"
            >
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">
                Scrivici
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Compila il modulo: il tuo messaggio arriva direttamente nella nostra casella email.
              </p>

              <div className="mt-8 space-y-5">
                <div>
                  <label htmlFor="cf-nome" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                    Nome e cognome *
                  </label>
                  <input
                    id="cf-nome"
                    type="text"
                    required
                    minLength={2}
                    value={form.nome}
                    onChange={set("nome")}
                    placeholder="Il tuo nome"
                    className={inputCls}
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                      Email *
                    </label>
                    <input
                      id="cf-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="nome@esempio.it"
                      className={inputCls}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-telefono" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                      Telefono
                    </label>
                    <input
                      id="cf-telefono"
                      type="tel"
                      value={form.telefono}
                      onChange={set("telefono")}
                      placeholder="+39 ..."
                      className={inputCls}
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="cf-messaggio" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                    Messaggio *
                  </label>
                  <textarea
                    id="cf-messaggio"
                    required
                    minLength={10}
                    rows={5}
                    value={form.messaggio}
                    onChange={set("messaggio")}
                    placeholder="Raccontaci la tua esigenza: intervento, ubicazione, tempistiche..."
                    className={`${inputCls} resize-y`}
                    data-testid="contact-message-textarea"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-8 flex w-full items-center justify-center gap-3 bg-brand-cyan px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-brand-teal transition hover:bg-brand-ice disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-teal/30 border-t-brand-teal" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    Invia il messaggio
                    <Send className="h-4 w-4" />
                  </>
                )}
              </button>

              {sent && (
                <p className="mt-5 border border-brand-cyan/40 bg-brand-cyan/10 px-4 py-3 text-center text-sm text-brand-ice" data-testid="contact-form-success">
                  Messaggio inviato con successo. Grazie, ti risponderemo al più presto.
                </p>
              )}
            </form>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
